import sileo from 'sileo';

const PlatformSettingsTemplates = new sileo.Model('hqzen', 'platform-settings-template');
const FullPlatformSettingsTemplates = new sileo.Model('hqzen', 'full-platform-settings-template');
const EmployeePlatformSettings = new sileo.Model('hqzen', 'employee-platform-settings');
const FullEmployeePlatformSettings = new sileo.Model('hqzen', 'full-employee-platform-settings');
const SyncAssignedTemplate = new sileo.Model('hqzen', 'sync-assigned-template');
const PlatformSettingsAssignees = new sileo.Model('hqzen', 'platform-settings-assignees');


/**
 * mapSettingsFormData is a helper function that
 * initializes data from the "copy-existing template" or "edit template" action,
 * as the keys differ between the source and destination.
 *
 * @param {*} data - data from the platform setting
 * @returns initialized data
 */
function mapSettingsFormData(data) {
    const {
        pk,
        name,
        assigned_employees_pks,

        //Desktop Settings
        is_desktop_timelogging_allowed,
        is_desktop_screenshot_collection_allowed,
        allow_desktop_app_config,
        screenshot_interval_lower,
        screenshot_interval_upper,
        is_desktop_user_activity_collection_allowed,
        is_track_idle_time_allowed,
        is_desktop_mouse_keyboard_collection_allowed,
        is_desktop_sites_restricted,
        is_desktop_apps_restricted,
        allow_desktop_website_config,
        is_desktop_remote_work_allowed,
        desktop_inactivity_check_countdown,
        desktop_timeout_countdown,
        desktop_inactivity_lower_interval,
        desktop_inactivity_upper_interval,
        desktop_timeout_lower_interval,
        desktop_timeout_upper_interval,
        fixed_inactivity_check_time,
        fixed_countdown_timeout,

        //Mobile Settings
        is_mobile_apps_restricted,
        is_mobile_remote_work_allowed,
        is_mobile_timelogging_allowed,
        is_mobile_user_activity_collection_allowed,
        mobile_allowed_idle_time,
        mobile_apps_default_timer,
        restricted_mobile_applications,
        whitelisted_mobile_applications,

        //Other Settings
        websites,
        applications,
        whitelisted_mobile_ip_addresses,
        whitelisted_desktop_ip_addresses,
    } = data;

    const mappedData = {
        pk: pk,
        name: name,
        assigned_employees_pks: assigned_employees_pks || [],

         //Desktop Settings
        allow_desktop_timelogging: is_desktop_timelogging_allowed || false,
        collect_desktop_screenshot: is_desktop_screenshot_collection_allowed || false,
        allow_desktop_app_config: allow_desktop_app_config || false,
        screenshot_interval_lower: screenshot_interval_lower || 0,
        screenshot_interval_upper: screenshot_interval_upper || 0,
        collect_desktop_user_activity: is_desktop_user_activity_collection_allowed || false,
        track_idle_time: is_track_idle_time_allowed || false,
        collect_desktop_mouse_keyboard: is_desktop_mouse_keyboard_collection_allowed || false,
        restrict_desktop_sites: is_desktop_sites_restricted || false,
        restrict_desktop_apps: is_desktop_apps_restricted || false,
        allow_desktop_website_config: allow_desktop_website_config || false,
        allow_desktop_remote_work: is_desktop_remote_work_allowed || false,
        desktop_timeout_countdown: desktop_timeout_countdown || 0,
        desktop_inactivity_check_countdown: desktop_inactivity_check_countdown || 0,
        desktop_inactivity_lower_interval: desktop_inactivity_lower_interval || 0,
        desktop_inactivity_upper_interval: desktop_inactivity_upper_interval || 0,
        desktop_timeout_lower_interval: desktop_timeout_lower_interval || 0,
        desktop_timeout_upper_interval: desktop_timeout_upper_interval || 0,
        fixed_inactivity_check_time: fixed_inactivity_check_time || false,
        fixed_countdown_timeout: fixed_countdown_timeout || false,

        //Mobile Settings
        allow_mobile_timelogging: is_mobile_timelogging_allowed || false,
        collect_mobile_user_activity: is_mobile_user_activity_collection_allowed,
        restrict_mobile_apps: is_mobile_apps_restricted || false,
        mobile_apps_default_timer: mobile_apps_default_timer || 0,
        user_mobile_allowed_idle_time: mobile_allowed_idle_time || 0,
        whitelisted_applications: whitelisted_mobile_applications,
        allow_mobile_remote_work: is_mobile_remote_work_allowed || false,
        restricted_applications: restricted_mobile_applications,

        // Other Settings
        websites: websites || [],
        applications: applications || [],
        ip_addresses: {
            'desktop': whitelisted_desktop_ip_addresses,
            'mobile': whitelisted_mobile_ip_addresses
        },
    };

    return JSON.parse(JSON.stringify(mappedData));
};

function separateOldAndNew(items, originalItems = []) {
    const result = { old: [], new: [] };

    items.forEach(item => {
        if ('pk' in item) {
            // Check if this item was actually modified by comparing with original
            const originalItem = originalItems.find(orig => orig.pk === item.pk);

            if (originalItem && JSON.stringify(originalItem) !== JSON.stringify(item)) {
                // Updated item - remove the pk and add to 'new'
                const { pk, ...items } = item;
                result.new.push(items);
            } else {
                // Item was not modified - push pk to 'old'
                result.old.push(item.pk);
            }
        } else {
            result.new.push(item);
        }
    });

    return result;
};

const state = {
    /**
     * use settings state to store data when viewing a PlatformSettingsTemplate
     * or WorkforceEmployeeSettings instance. Can also be used to save initial
     * configuration from a PlatformSettingsTemplate or WorkforceEmployeeSettings
     * instance before editing values for settingsFormData
    */
    settings: {},
    /**
     * settingsFormData should hold all data needed for CREATING PlatformSettingsTemplate
     * or when EDITING for a PlatformSettingsTemplate or WorkforceEmployeeSettings
     * Keep snake-case of settingsFormData properties for convenience
     * when sending form data to the backend
    */
    settingsFormData: {
        pk: '',
        name: '',
        assigned_employees_pks: [],

        // Desktop Settings
        allow_desktop_timelogging: false,
        collect_desktop_screenshot: false,
        allow_desktop_app_config: false,
        screenshot_interval_lower: 0,
        screenshot_interval_upper: 0,
        collect_desktop_user_activity: false,
        track_idle_time: false,
        collect_desktop_mouse_keyboard: false,
        restrict_desktop_sites: true,
        restrict_desktop_apps: true,
        allow_desktop_website_config: false,
        allow_desktop_remote_work: true,
        desktop_timeout_countdown: 0,
        desktop_inactivity_check_countdown: 0,
        desktop_inactivity_lower_interval: 0,
        desktop_inactivity_upper_interval: 0,
        desktop_timeout_lower_interval: 0,
        desktop_timeout_upper_interval: 0,
        fixed_inactivity_check_time: true,
        fixed_countdown_timeout: true,

        // Mobile Settings
        allow_mobile_timelogging: false,
        collect_mobile_user_activity: false,
        restrict_mobile_apps: false,
        mobile_apps_default_timer: 0,
        user_mobile_allowed_idle_time: 0,
        whitelisted_applications: [],
        allow_mobile_remote_work: true,
        restricted_applications: [],

        // Other Settings
        websites: [],
        applications: [],
        ip_addresses: {
            'desktop': [],
            'mobile': []
        }
    },
    // Stores the formatted settings data to be used for creating a new platform settings template
    formattedSettingsData: {},

    /** assignees should store data related to selected partners/assignees
     * for a PlatformSettingsTemplate or WorkforceEmployeeSettings instance.
     */
    assignees: {
        // existingAssignedPartnersPks should store all partners
        // that have been assigned to a platform settings (PS) already.
        existingAssignedPartnersPks: [],
        // allSelectedAssigneesIds should store the IDs
        // of the selected partners for the current platform settings
        // including to be moved partners (partners that already have assigned PS).
        // This is useful for displaying the list of all selected partners.
        allSelectedAssigneesIds: [],
        // Should store the raw data of the currently selected partners
        // Used in: PlatformSettingsSummaryPage.vue
        currentSelectedPartnersRaw: [],
        selectedPreference: '',
        saveTemplateChoice: null,
    },
    // used to store current value for toggle choice in GeneralPlatformSettingsPage
    chosenToggle: 'create-new',
    manageMobileApplications: false
};

const getters = {
    settingsFormData: state => state.settingsFormData,
    assignees: state => state.assignees,
    settings: state => state.settings,
};

const mutations = {

    setDesktopSettings(state, desktopSettings) {
        Object.assign(state.settingsFormData, {
            allow_desktop_timelogging: desktopSettings.allow_desktop_timelogging,
            collect_desktop_screenshot: desktopSettings.collect_desktop_screenshot,
            allow_desktop_app_config: desktopSettings.allow_desktop_app_config,
            screenshot_interval_lower: desktopSettings.screenshot_interval_lower,
            screenshot_interval_upper: desktopSettings.screenshot_interval_upper,
            collect_desktop_user_activity: desktopSettings.collect_desktop_user_activity,
            track_idle_time: desktopSettings.track_idle_time,
            collect_desktop_mouse_keyboard: desktopSettings.collect_desktop_mouse_keyboard,
            restrict_desktop_sites: desktopSettings.restrict_desktop_sites,
            restrict_desktop_apps: desktopSettings.restrict_desktop_apps,
            allow_desktop_website_config: desktopSettings.allow_desktop_website_config,
            allow_desktop_remote_work: desktopSettings.allow_desktop_remote_work,
            desktop_timeout_countdown: desktopSettings.desktop_timeout_countdown,
            desktop_inactivity_check_countdown: desktopSettings.desktop_inactivity_check_countdown,
            desktop_inactivity_lower_interval: desktopSettings.desktop_inactivity_lower_interval,
            desktop_inactivity_upper_interval: desktopSettings.desktop_inactivity_upper_interval,
            desktop_timeout_lower_interval: desktopSettings.desktop_timeout_lower_interval,
            desktop_timeout_upper_interval: desktopSettings.desktop_timeout_upper_interval,
            fixed_inactivity_check_time: desktopSettings.fixed_inactivity_check_time,
            fixed_countdown_timeout: desktopSettings.fixed_countdown_timeout,
            applications: desktopSettings.applications,
            websites: desktopSettings.websites,
            ip_addresses: {
                desktop: desktopSettings.ip_addresses.desktop,
                mobile: desktopSettings.ip_addresses.mobile,
            },
        });
    },

    setMobileSettings(state, mobileSettings) {
        const mobileFields = [
            'allow_mobile_timelogging',
            'collect_mobile_user_activity',
            'restrict_mobile_apps',
            'mobile_apps_default_timer',
            'user_mobile_allowed_idle_time',
            'whitelisted_applications',
            'restricted_applications',
            'allow_mobile_remote_work',
        ];

        mobileFields.forEach(key => {
            state.settingsFormData[key] = mobileSettings[key];
        });

        state.settingsFormData.ip_addresses = {
            ...state.settingsFormData.ip_addresses,
            mobile: mobileSettings.ip_addresses
        };
    },

    setPlatformSettingsAssignees(state, assignees) {
        Object.keys(assignees).forEach((key) => {
            if (key in state.assignees) {
                state.assignees[key] = assignees[key];
            }
        });

        if ('assigned_employees_pks' in assignees) {
            state.settingsFormData.assigned_employees_pks = assignees.assigned_employees_pks;
        }
    },

    setSettings(state, data) {
        state.settings = mapSettingsFormData(data);
    },

    setSettingsFormDataName(state, data) {
        state.settingsFormData.name = data;
    },

    setSettingsName(state, data) {
        state.settings.name = data;
    },

    setMobileManageApplications(state, data) {
        state.manageMobileApplications = data;
    },

    setSelectedToggle(state, data) {
        state.chosenToggle = data;
    },

    setManageMobileApplications(state, data) {
        state.manageMobileApplications = data;
    },

    setGeneralSettings(state, data) {
        state.settingsFormData.allow_mobile_timelogging = data.allow_mobile_timelogging;
        state.settingsFormData.allow_desktop_timelogging = data.allow_desktop_timelogging;
    },

    setSettingsFormData(state, data) {
        state.settingsFormData = mapSettingsFormData(data);
    },

    // Used in CreateStaffConfigPage to set the formattedSettingsData
    // before creating a new platform settings template
    setStaffConfigData(state, data) {
        state.formattedSettingsData = data
    },

    formatSettingsFormData(state, payload) {
        const data = { ...state.settingsFormData };
        const originalSettings = state.settings;

        data.name = data.name || payload.name;
        data.company_id = payload.company_id;
        data.workforce_id = payload.workforce_id;
        data.creator_id = payload.creator_id;

        if (!data.allow_desktop_timelogging) {
            Object.assign(data, {
                collect_desktop_screenshot: false,
                allow_desktop_app_config: false,
                collect_desktop_user_activity: false,
                collect_desktop_mouse_keyboard: false,
                allow_desktop_website_config: false,
                restrict_desktop_apps: false,
                allow_desktop_remote_work: true,
                track_idle_time: false,
            });
        }

        if (!data.allow_mobile_timelogging) {
            Object.assign(data, {
                collect_mobile_user_activity: false,
                allow_mobile_remote_work: true,
                restrict_mobile_apps: false,
            });
        }

        if (!data.desktop_timeout_lower_interval && !data.desktop_timeout_upper_interval) {
            delete data.desktop_timeout_lower_interval;
            delete data.desktop_timeout_upper_interval;
        }

        if (!data.allow_desktop_website_config) data.websites = [];
        if (!data.allow_desktop_app_config) data.applications = [];

        // map IP addresses to have the same starting IP if the
        // configured IP address is not of the Range type. Desktop
        // App still checks the ending IP even if it is a singular IP set.
        let desktop_ips = data.ip_addresses.desktop.map(ip => {
            return {
                ...ip,
                ending_ip: ip.ending_ip ? ip.ending_ip : ip.starting_ip
            };
        });
        let mobile_ips = data.ip_addresses.mobile;

        if (Array.isArray(data.websites)) {
            data.websites = separateOldAndNew(data.websites, originalSettings.websites);
        }

        if (Array.isArray(data.applications)) {
            data.applications = separateOldAndNew(data.applications, originalSettings.applications);
        }

        if (Array.isArray(desktop_ips)) {
            desktop_ips = separateOldAndNew(desktop_ips, originalSettings.ip_addresses?.desktop);
        }

        if (Array.isArray(mobile_ips)) {
            mobile_ips = separateOldAndNew(mobile_ips, originalSettings.ip_addresses?.mobile);
        }

        data.ip_addresses = { desktop: desktop_ips, mobile: mobile_ips };

        if (Array.isArray(data.whitelisted_applications)) {
            data.whitelisted_applications = JSON.stringify(
                separateOldAndNew(
                    data.whitelisted_applications,
                    originalSettings?.whitelisted_applications)
                );
        }

        if (Array.isArray(data.restricted_applications)) {
            data.restricted_applications = JSON.stringify(
                separateOldAndNew(
                    data.restricted_applications,
                    originalSettings?.restricted_applications)
                );
        }
        state.formattedSettingsData = data;
    },

    resetSettingsFormData(state) {
        state.settingsFormData = {
            name: '',
            assigned_employees_pks: [],

            // Desktop Settings
            allow_desktop_timelogging: false,
            collect_desktop_screenshot: false,
            allow_desktop_app_config: false,
            screenshot_interval_lower: 0,
            screenshot_interval_upper: 0,
            collect_desktop_user_activity: false,
            track_idle_time: false,
            collect_desktop_mouse_keyboard: false,
            restrict_desktop_sites: true,
            restrict_desktop_apps: true,
            allow_desktop_website_config: false,
            allow_desktop_remote_work: true,
            desktop_timeout_countdown: 0,
            desktop_inactivity_check_countdown: 0,
            desktop_inactivity_lower_interval: 0,
            desktop_inactivity_upper_interval: 0,
            desktop_timeout_lower_interval: 0,
            desktop_timeout_upper_interval: 0,
            fixed_inactivity_check_time: true,
            fixed_countdown_timeout: true,

            // Mobile Settings
            allow_mobile_timelogging: false,
            collect_mobile_user_activity: false,
            restrict_mobile_apps: false,
            mobile_apps_default_timer: 0,
            user_mobile_allowed_idle_time: 0,
            whitelisted_applications: [],
            allow_mobile_remote_work: true,
            restricted_applications: [],

            // Other Settings
            websites: [],
            applications: [],
            ip_addresses: {
                'desktop': [],
                'mobile': []
            },
        };

        state.assignees = {
            existingAssignedPartnersPks: [],
            allSelectedAssigneesIds: [],
            currentSelectedPartnersRaw: [],
            selectedPreference: '',
            saveTemplateChoice: null,
        };

        state.settings = {};
        state.formattedSettingsData = {};
        state.manageMobileApplications = false;
    },
};

const actions = {
    fetchEmployeePlatformSettings(context, filters) {
        return EmployeePlatformSettings.objects.filter(filters);
    },

    getFullEmployeePlatformSettings(context, pk) {
        return FullEmployeePlatformSettings.objects.get(pk);
    },

    updatePlatformSettings({ state }, { pk }) {
        const form = new FormData();
        let data = state.formattedSettingsData;

        for (let field in data) {
            let value = data[field];
            if ([
                'websites',
                'applications',
                'restricted_websites',
                'whitelisted_websites',
                'restricted_apps',
                'ip_addresses',
            ].includes(field)) {
                value = JSON.stringify(value);
            }

            form.append(field, value);
        }
        return FullEmployeePlatformSettings.objects.update({ 'pk': pk }, form);
    },

    fetchPlatformSettings({ commit }, filters) {
        return PlatformSettingsTemplates.objects.filter(filters);
    },

    deletePlatformSettings(context, filters) {
        return PlatformSettingsTemplates.objects.delete(filters);
    },

    getFullPlatformSettingsTemplate(context, pk) {
        return FullPlatformSettingsTemplates.objects.get(pk);
    },

    fetchPlatformSettingsTemplatesDetails(context, filters) {
        return FullPlatformSettingsTemplates.objects.filter(filters);
    },

    createPlatformSettingsTemplate({ state }) {
        const form = new FormData();
        const data = state.formattedSettingsData;

        for (const field in data) {
            let value = data[field];

            if ([
                'restricted_websites',
                'whitelisted_websites',
                'websites',
                'applications',
                'restricted_apps',
                'ip_addresses',
                'assigned_employees_pks'
            ].includes(field)) {
                value = JSON.stringify(value);
            }

            form.append(field, value);
        }
        return FullPlatformSettingsTemplates.objects.create(form);
    },

    updatePlatformSettingsTemplate({ state }, { pk }) {
        const form = new FormData();
        let data = state.formattedSettingsData;

        for (let field in data) {
            let value = data[field];
            if ([
                'restricted_websites',
                'whitelisted_websites',
                'websites',
                'applications',
                'restricted_apps',
                'ip_addresses',
                'assigned_employees_pks'
            ].includes(field)) {
                value = JSON.stringify(value);
            }

            form.append(field, value);
        }
        form.append('save_template_choice', state.assignees.saveTemplateChoice);
        return FullPlatformSettingsTemplates.objects.update({ 'pk': pk }, form);
    },

    updateWorkforceEmployeeSettings(context, pk) {
        return SyncAssignedTemplate.objects.update({ 'pk': pk });
    },

    fetchPlatformSettingsAssignees(context, filters) {
        return PlatformSettingsAssignees.objects.filter(filters);
    },

    updatePlatformSettingAssignees(context, { pk, data }) {
        const form = new FormData();

        for (let field in data) {
            let value = data[field];
            if ([
                'added_assignee_ids',
                'removed_assignee_ids'
            ].includes(field)) {
                value = JSON.stringify(value);
            }

            form.append(field, value);
        }

        return PlatformSettingsAssignees.objects.update({ 'pk': pk }, form);
    }
};

export default {
    namespaced: true,
    actions,
    state,
    getters,
    mutations
};
